'use client'

import { css, styled } from 'styled-components'

import { MultiLineWithEllipsis } from '@b-stock/fe-core/bstock-next/components'
import { Button, ButtonAsA } from '@b-stock/fe-core/bstock-react'
import {
  Breakpoints,
  Colors,
  Shadows,
  Typography,
} from '@b-stock/fe-core/design-system'
import { designColors } from '@b-stock/fe-core/theme'

import {
  COMPARE_CARD_STACK_WIDTH,
  COMPARE_CARD_STACKED_ATTR,
} from '../constants'

const stackedFooter = css`
  flex-direction: column;
  align-items: stretch;
`

const stackedActions = css`
  justify-content: flex-end;
  margin-left: 0;
`

const stackedCard = `[${COMPARE_CARD_STACKED_ATTR}]`

export const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 40;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem 1rem;
`

export const Panel = styled.aside`
  position: relative;
  width: min(1360px, 96vw);
  max-height: calc(100dvh - 4rem);
  background-color: ${Colors.Semantic.Neutral.white};
  border-radius: 0.75rem;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  /* Backdrop pads 1rem on each side, so match that here instead of 96vw —
     otherwise the panel is wider than the space Backdrop actually leaves for
     it and overflows off the edges of narrow (mobile) viewports. */
  @media ${Breakpoints.max.small} {
    width: min(1360px, calc(100vw - 2rem));
  }
`

export const HeaderBar = styled.header`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid ${Colors.Semantic.Neutral.light_grey};
  flex-shrink: 0;
`

export const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1 1 auto;
  min-width: 0;
`

export const Eyebrow = styled.span`
  ${Typography.Overline1}
  color: ${Colors.Semantic.Neutral.dark_grey};
`

export const Title = styled.span`
  ${Typography.Title1}
  color: ${Colors.Semantic.Neutral.black};
`

export const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: none;
  background: none;
  padding: 0;
  color: ${Colors.Semantic.Neutral.black};
  font-size: 1.25rem;
  cursor: pointer;
  flex-shrink: 0;
`

export const ScrollArea = styled.div`
  /* Published here so the grid tracks, the sticky label column's own inset and
     the snap offset are read from one place and cannot drift apart. */
  --compare-inline-inset: 2rem;
  --compare-label-track: 150px;
  --compare-value-track: 200px;

  flex: 1 1 auto;
  overflow: auto;

  /* Deliberately no left padding. Container padding sits BEFORE the sticky
     label column's left: 0, so it is outside that column's opaque background
     and scrolled-past value cells bleed through the gap. The inset moves onto
     the sticky cells themselves instead. Right padding stays: nothing sticks
     to that edge. */
  padding: 1rem var(--compare-inline-inset) 1.5rem 0;

  /* A value column is wider than the label column, so a freely scrolled column
     always leaves a tail exposed beside the label, which reads as an orphaned
     fragment of the label's own row. Snapping each column to the label's right
     edge means a column is either fully behind the label or fully beside it. */
  scroll-snap-type: x mandatory;
  scroll-padding-left: calc(
    var(--compare-label-track) + var(--compare-inline-inset)
  );

  @media ${Breakpoints.max.small} {
    --compare-inline-inset: 1rem;
    --compare-label-track: 6rem;
    --compare-value-track: 10rem;
  }
`

export const BannerArea = styled.div`
  padding-left: var(--compare-inline-inset);
`

export const Grid = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns:
    calc(var(--compare-label-track) + var(--compare-inline-inset))
    repeat(
      ${({ $columns }) => $columns},
      minmax(var(--compare-value-track), 1fr)
    );
  align-items: stretch;
  min-width: min-content;

  /* Runway so the last column can reach the label's edge; see
     useCompareColumnRunway for why its size cannot be written in CSS. */
  padding-right: var(--compare-runway, 0px);
`

// Covers the same grid column as LabelCell so the listing cards do not slide
// over the label column during horizontal scroll. HeaderCard is positioned and
// comes later in the DOM, so this needs a z-index to stay in front of it.
export const HeaderCornerCell = styled.div`
  position: sticky;
  left: 0;
  z-index: 2;
  background-color: ${Colors.Semantic.Neutral.white};
`

export const HeaderCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  /* The footer's shape keys off this card's width rather than the viewport's:
     the same viewport gives two selected listings twice the column width of
     four. Every column is equal width, so all cards change together. */
  container-type: inline-size;
  gap: 0.375rem;
  padding: 0.5rem;
  margin: 0 0.5rem 0.75rem;
  box-sizing: border-box;
  max-width: 264px;
  border: 1px solid ${Colors.Semantic.Neutral.light_grey};
  border-radius: 0.5rem;
  background-color: ${Colors.Semantic.Neutral.white};
  ${Shadows.MultipleCards}
`

export const CardMain = styled.div`
  display: flex;
  gap: 0.5rem;
  min-width: 0;
  padding-right: 1.125rem;
`

export const CardThumb = styled.div`
  flex-shrink: 0;
  > img {
    width: 3rem;
    height: 3rem;
    object-fit: cover;
    border-radius: 0.375rem;
  }
`

export const CardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;

  > * {
    flex: 0 0 auto;
  }
`

export const CardTitle = styled(MultiLineWithEllipsis)`
  ${Typography.Body3_Semibold}
  color: ${Colors.Semantic.Neutral.black};

  overflow-wrap: anywhere;
`

export const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.375rem 0.5rem;
  margin-top: auto;

  /* No card width holds every amount the platform can print, and a wrapped row
     reads better than a split number. */
  flex-wrap: wrap;

  @container (max-width: ${COMPARE_CARD_STACK_WIDTH}) {
    ${stackedFooter}
  }

  ${stackedCard} & {
    ${stackedFooter}
  }
`

export const CardActions = styled.div`
  display: flex;
  align-items: stretch;
  gap: 0.375rem;
  flex-shrink: 0;
  /* Holds the right edge whether the footer is one row or two. */
  margin-left: auto;

  @container (max-width: ${COMPARE_CARD_STACK_WIDTH}) {
    ${stackedActions}
  }

  ${stackedCard} & {
    ${stackedActions}
  }
`

export const CardBid = styled.span`
  ${Typography.Body1_Semibold}
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem;
  color: ${Colors.Semantic.Neutral.black};
  min-width: 0;

  /* An amount split between its digits is unreadable; it takes a row instead. */
  white-space: nowrap;
`

export const CurrencyChip = styled.span`
  ${Typography.Body4_Semibold}
  flex-shrink: 0;
  padding: 0.0625rem 0.1875rem;
  border-radius: 0.1875rem;
  color: ${Colors.Semantic.Warning.highlight};
  background-color: ${Colors.Semantic.Warning.background};
  white-space: nowrap;
`

export const RemoveButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  padding: 0;
  border: none;
  background: none;
  color: ${Colors.Semantic.Neutral.black};
  font-size: 1rem;
  cursor: pointer;
`

export const WatchlistButton = styled.button<{ $watchlisted?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  border: 1px solid ${designColors.primary.default};
  border-radius: 0.25rem;
  background-color: ${Colors.Semantic.Neutral.white};
  color: ${designColors.primary.default};
  cursor: pointer;
  flex-shrink: 0;

  ${({ $watchlisted }) =>
    $watchlisted &&
    css`
      background-color: ${designColors.primary.backgroundLightBlue};
    `}

  &:hover:not(:disabled) {
    background-color: ${designColors.primary.backgroundLightBlue};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`

export const BidButton = styled(Button)`
  min-width: 3rem;
  flex-shrink: 0;
`

export const BidLink = styled(ButtonAsA)`
  min-width: 3rem;
  flex-shrink: 0;
`

export const ManifestLink = styled(ButtonAsA)`
  ${Typography.Body2Semibold}
`

export const SectionHeader = styled.div`
  ${Typography.Body2Semibold}
  grid-column: 1 / -1;
  padding: 0.875rem 0.75rem;
  /* The scroll area only pads its right edge now, so the band takes its left
     inset here instead — otherwise it runs full-bleed left but stops short on
     the right. SectionLabel's sticky offset matches this so the text does not
     shift as the band scrolls under it. */
  margin-left: var(--compare-inline-inset);
  margin-top: 0.75rem;
  color: ${Colors.Semantic.Neutral.black};
  background-color: ${Colors.Semantic.Neutral.light_grey};
`

export const SectionLabel = styled.span`
  position: sticky;
  left: calc(0.75rem + var(--compare-inline-inset));
  display: inline-block;
`

const cellBase = css`
  padding: 1rem 0.75rem;
  border-bottom: 1px solid ${Colors.Semantic.Neutral.light_grey};
  display: flex;
  align-items: center;
`

export const LabelCell = styled.div`
  ${cellBase}
  ${Typography.Body2Semibold}
  position: sticky;
  left: 0;
  z-index: 1;
  min-width: 0;
  overflow-wrap: anywhere;
  /* Carries the scroll area's missing left inset so this cell's opaque
     background reaches the panel edge with nothing showing through beside it. */
  padding-left: calc(0.75rem + var(--compare-inline-inset));
  background-color: ${Colors.Semantic.Neutral.white};
  color: ${Colors.Semantic.Neutral.dark_grey};
`

export const ValueCell = styled.div<{ $winner?: boolean; $hasValue?: boolean }>`
  ${cellBase}
  ${Typography.Body2}
  scroll-snap-align: start;
  gap: 0.375rem;
  color: ${Colors.Semantic.Neutral.dark_grey};

  ${({ $hasValue }) =>
    $hasValue &&
    css`
      ${Typography.Body2Semibold}
      color: ${Colors.Semantic.Neutral.black};
    `}

  ${({ $winner }) =>
    $winner &&
    css`
      ${Typography.Body2Semibold}
      color: ${Colors.Semantic.Success.copy};
    `}
`

export const WinnerCheck = styled.span`
  color: ${Colors.Semantic.Success.copy};
  display: inline-flex;
`

export const QuotePending = styled.span`
  display: inline-flex;
  width: 4rem;
  color: ${Colors.Semantic.Neutral.dark_grey};
`
